import { makeStyles } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import Button from '../components/Button'
import type { DialogProps } from '../components/Dialog'
import Dialog, { DialogActions, DialogContent, DialogTitle } from '../components/Dialog'
import Grid from '../components/Grid'
import SelectableChipLarge from '../components/SelectableChip/SelectableChipLarge'
import Typography from '../components/Typography'
import { useSearchContactsWithIntegrationQuery } from '@paintscout/api'
import { useSnackbar } from 'notistack'
import Spinner from '../components/Spinner'

export interface SelectContactDetailsDialogProps extends DialogProps {
  contactId: string
  onConfirm: (values: Record<string, string>) => void
  onCancel: (event: React.MouseEvent<HTMLElement>) => void
}

const useStyles = makeStyles((theme) => ({
  root: {},
  deals: {},
  selectAllButton: {
    marginRight: theme.spacing(2)
  }
}))

function SelectContactDetailsDialog({
  onCancel,
  onConfirm,
  contactId,
  ...otherProps
}: SelectContactDetailsDialogProps) {
  const [selectedDetailIds, setSelectedDetailIds] = useState<string[]>([])
  const { enqueueSnackbar } = useSnackbar()
  const [open, setOpen] = useState(true)
  const { data, error, loading } = useSearchContactsWithIntegrationQuery({
    variables: {
      integration: 'service_titan_contacts',
      integrationQuery: contactId,
      paintscoutQuery: '',
      useIntegration: true
    }
  })

  let details = data?.integration?.results ?? []

  useEffect(() => {
    details = data?.integration?.results ?? []
    if (details.length === 0 && !loading) {
      handleConfirm(null)
    }
  }, [data])

  useEffect(() => {
    if (error) {
      enqueueSnackbar('Error loading contact details', { variant: 'error' })
    }
  }, [error])

  const classes = useStyles(otherProps)

  function handleSelectAll(_ev: any) {
    // Select one of each type of detail from the list
    const selectedIds = details.reduce((acc, detail) => {
      const correctedType = correctType(detail.type)
      return correctedType && !acc[correctedType]
        ? {
            ...acc,
            [correctedType]: detail.id
          }
        : acc
    }, {})
    setSelectedDetailIds(Object.values(selectedIds))
  }

  async function handleConfirm(_ev: any) {
    setOpen(false)
    const selectedDetails = details.reduce((acc, detail) => {
      const correctedType = correctType(detail.type)
      return selectedDetailIds.includes(detail.id) && correctedType
        ? {
            ...acc,
            [correctedType]: detail.value
          }
        : acc
    }, {})
    onConfirm(selectedDetails)
  }
  console.log({ details, selectedDetailIds })
  return (
    <Dialog open={open} onClose={onCancel}>
      <DialogTitle>Choose Contact Details</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} justifyContent="space-between" direction="column">
          <Grid item xs={12}>
            <Typography variant={'body1'}>Select the contact details for this customer.</Typography>
          </Grid>
          <Grid item xs={12} style={{ maxWidth: '700px' }}>
            <Grid container justifyContent="space-between" direction="column">
              {!details.length && loading && <Spinner />}
              {details.map((detail) => {
                const percent = detail?.deal_stage?.percent ?? 1
                const disabled = percent === 100 || percent === 0
                return (
                  <Grid item className={classes.deals} key={detail.id}>
                    <SelectableChipLarge
                      label={`${detail.value}`}
                      sublabel={`${detail.id} - ${detail.type}`}
                      selected={selectedDetailIds.includes(detail.id)}
                      disabled={disabled}
                      onClick={() => {
                        setSelectedDetailIds((selectedDetailIds) =>
                          selectedDetailIds.includes(detail.id)
                            ? selectedDetailIds.filter((existingDetailId) => existingDetailId !== detail.id)
                            : [...selectedDetailIds, detail.id]
                        )
                      }}
                    />
                  </Grid>
                )
              })}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions
        leftButton={
          <Button onClick={onCancel} variant="textNoPadding">
            Cancel
          </Button>
        }
      >
        <Button onClick={handleSelectAll} disabled={loading} variant={'contained'} className={classes.selectAllButton}>
          Select All
        </Button>
        <Button onClick={handleConfirm} disabled={loading} variant={'contained'}>
          Done
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function correctType(type: string): string {
  let correctedType = ''
  switch (type) {
    case 'Email':
      correctedType = 'email'
      break
    case 'MobilePhone':
      correctedType = 'phone_number'
      break
    case 'Phone':
      correctedType = 'phone_number'
      break
    case 'Fax':
      correctedType = 'phone_number'
      break
    default:
      correctedType = null
  }
  return correctedType
}

export default SelectContactDetailsDialog
